import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkle, Star, MapPin, Buildings, GraduationCap, ArrowRight, CurrencyInr } from "@phosphor-icons/react";
import { getNewColleges, ASSET_BASE } from "../lib/api";
import { LeadForm } from "./LeadForm";

// ── Type chip palette (top-left of card, replacing "Featured")
const TYPE_CHIP = {
  govt:       { label: "GOVT",    cls: "bg-emerald-500 text-white" },
  government: { label: "GOVT",    cls: "bg-emerald-500 text-white" },
  private:    { label: "PRIVATE", cls: "bg-violet-600 text-white" },
  deemed:     { label: "DEEMED",  cls: "bg-blue-600 text-white" },
  trust:      { label: "TRUST",   cls: "bg-amber-500 text-white" },
};

/**
 * PhotoStack — cross-fading, ken-burns photo carousel for a single college.
 *
 *  · Sources up to 3 photos (image_paths + header fallback).
 *  · 2-second dwell + 400 ms cross-fade per user spec.
 *  · Subtle Ken-Burns zoom (transform: scale + translate) on the active slide.
 *  · Single photo → no cycling, no timer.
 *  · Broken image → onError skips the slide.
 */
const PhotoStack = ({ code, photoCount = 0, hasHeader = false, paused = false }) => {
  const urls = useMemo(() => {
    const arr = [];
    // Prefer image_paths (Google Places photos) for variety, then fall back to
    // the composited header image. Cap at 3.
    for (let i = 0; i < Math.min(photoCount, 3); i += 1) {
      arr.push(`${ASSET_BASE}/colleges/${encodeURIComponent(code)}/images/${i}`);
    }
    if (arr.length === 0 && hasHeader) {
      arr.push(`${ASSET_BASE}/colleges/${encodeURIComponent(code)}/header`);
    }
    return arr;
  }, [code, photoCount, hasHeader]);

  const [idx, setIdx] = useState(0);
  const [errored, setErrored] = useState({});

  const visibleUrls = urls.filter((_, i) => !errored[i]);

  useEffect(() => {
    if (paused || visibleUrls.length < 2) return undefined;
    const t = setInterval(() => {
      setIdx((prev) => (prev + 1) % visibleUrls.length);
    }, 4000); // 4 s dwell (updated per user feedback)
    return () => clearInterval(t);
  }, [paused, visibleUrls.length]);

  if (visibleUrls.length === 0) {
    return (
      <div className="absolute inset-0 bg-gradient-to-br from-violet-100 to-orange-50 flex items-center justify-center">
        <Buildings size={44} weight="fill" className="text-violet-300" />
      </div>
    );
  }

  return (
    <>
      {visibleUrls.map((url, i) => (
        <img
          key={url}
          src={url}
          alt=""
          loading="lazy"
          onError={() => setErrored((e) => ({ ...e, [i]: true }))}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[800ms] ease-in-out ${
            i === idx ? "opacity-100 animate-[ken-burns_10s_ease-in-out_infinite]" : "opacity-0"
          }`}
        />
      ))}
      {/* dark scrim at bottom so name/city stays legible on any photo */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent" />
    </>
  );
};

// Formats fees as ₹1.25 L / ₹18 L / ₹1.1 Cr etc. Falls back to null.
const formatFees = (n) => {
  if (n == null || Number.isNaN(Number(n))) return null;
  const v = Number(n);
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)} L`;
  return `₹${v.toLocaleString("en-IN")}`;
};

const PropertyCard = ({ c, onEnquire, onOpen }) => {
  const [hovered, setHovered] = useState(false);
  const type = (c.college_type || "").toLowerCase();
  const chip = TYPE_CHIP[type] || { label: (type || "COLLEGE").toUpperCase(), cls: "bg-zinc-800 text-white" };
  const fees = formatFees(c.fees_govt);

  return (
    <div
      className="group snap-start shrink-0 w-[300px] sm:w-[320px] md:w-[340px] bg-white rounded-2xl border border-zinc-200/80 shadow-[0_4px_14px_-4px_rgba(15,15,20,0.10)] hover:shadow-[0_16px_36px_-8px_rgba(15,15,20,0.22)] hover:-translate-y-1.5 transition-all duration-300 cursor-pointer overflow-hidden"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onOpen(c)}
      data-testid={`fresh-card-${c.college_code}`}
    >
      {/* Photo area — 65% of card height */}
      <div className="relative h-[220px] bg-zinc-100 overflow-hidden">
        <PhotoStack code={c.college_code} photoCount={c.photo_count || 0} hasHeader={c.has_header} paused={hovered} />

        {/* Top-left type chip (replaces Featured) */}
        <span
          className={`absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] ${chip.cls} shadow-[0_2px_6px_rgba(0,0,0,0.15)]`}
          data-testid={`fresh-card-type-${c.college_code}`}
        >
          <Sparkle size={11} weight="fill" /> {chip.label}
        </span>

        {/* Google rating pill — bottom-left */}
        {c.google_rating != null && (
          <span
            className="absolute bottom-3 left-3 inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm rounded-full px-2.5 py-1 text-[11px] font-bold text-[#09090B] shadow-[0_2px_6px_rgba(0,0,0,0.15)]"
            data-testid={`fresh-card-rating-${c.college_code}`}
          >
            <Star size={11} weight="fill" className="text-amber-500" />
            {Number(c.google_rating).toFixed(1)}
            {c.google_rating_count > 0 && (
              <span className="font-normal text-zinc-500 text-[10px]">({c.google_rating_count})</span>
            )}
          </span>
        )}

        {/* College logo — bottom-right, mimicking the Godrej brand tile */}
        {c.has_logo && (
          <div className="absolute bottom-3 right-3 w-11 h-11 bg-white rounded-xl shadow-[0_2px_6px_rgba(0,0,0,0.2)] flex items-center justify-center overflow-hidden ring-1 ring-black/5">
            <img
              src={`${ASSET_BASE}/colleges/${encodeURIComponent(c.college_code)}/logo`}
              alt=""
              className="w-full h-full object-contain p-1"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
        )}
      </div>

      {/* Info footer */}
      <div className="p-4 space-y-1.5">
        {/* Small caption line — affiliating university (replaces "Godrej Properties") */}
        <div className="text-[11px] text-zinc-500 line-clamp-1 flex items-center gap-1">
          <GraduationCap size={12} className="shrink-0" />
          <span className="truncate">{c.affiliate_university || c.state || "—"}</span>
        </div>

        {/* Bold name — reserved 2-line height so ENQUIRE FEES aligns across cards */}
        <div className="font-bold text-[15px] text-[#09090B] leading-tight line-clamp-2 min-h-[38px]" data-testid={`fresh-card-name-${c.college_code}`}>
          {c.primary_name || "—"}
        </div>

        {/* Pin + city */}
        <div className="text-[12px] text-zinc-600 flex items-center gap-1">
          <MapPin size={11} weight="fill" className="text-[#FF2A00]" />
          <span className="truncate">
            {c.city || "—"}{c.state && c.city ? ` · ${c.state}` : c.state || ""}
          </span>
        </div>

        {/* Bottom row: NMC code + est year + seats */}
        <div className="flex items-center gap-2 pt-1.5 text-[10px]">
          {c.college_code?.startsWith("NMC_") && (
            <span className="font-mono font-bold text-[#FF2A00] bg-[#FF2A00]/10 rounded-full px-2 py-0.5" title="NMC 2026 deterministic code">
              {c.college_code}
            </span>
          )}
          {c.established_year && (
            <span className="text-zinc-500">Est. <span className="font-mono font-bold text-[#09090B]">{parseInt(c.established_year, 10)}</span></span>
          )}
          <span className="ml-auto text-zinc-500">
            <span className="font-mono font-bold text-[#09090B]">{c.mbbs_seats || "—"}</span> seats
          </span>
        </div>

        {/* Fees line OR Enquire CTA when unknown */}
        {fees ? (
          <div className="pt-2 text-[15px] font-bold text-emerald-700 flex items-center gap-1" data-testid={`fresh-card-fees-${c.college_code}`}>
            <CurrencyInr size={14} weight="bold" /> Starting from {fees}<span className="text-[11px] font-normal text-zinc-500 ml-1">/yr</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEnquire(c); }}
            data-testid={`fresh-card-enquire-${c.college_code}`}
            className="w-full mt-2 h-9 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white text-[12px] font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all shadow-[0_2px_8px_-2px_rgba(234,88,12,0.6)]"
          >
            <CurrencyInr size={13} weight="bold" /> Enquire Fees
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Homepage rail — 25 newly-approved AY 2026-27 medical colleges shown as
 * property-listing style cards. Auto-hides when the API returns zero.
 */
export const FreshCollegesRail = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enquireCollege, setEnquireCollege] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getNewColleges(2026, true, 30)
      .then((r) => setItems(r.data.colleges || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!items || items.length === 0) return null;

  const ayLabel = "AY 2026-27";

  return (
    <>
      <section className="border-b border-zinc-200 py-14 bg-gradient-to-b from-white to-orange-50/30" data-testid="fresh-colleges-rail">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF2A00] flex items-center gap-2">
                FRESH ADMISSIONS
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#FF2A00] text-white text-[9px] font-bold uppercase tracking-[0.15em] animate-pulse">
                  <span className="w-1 h-1 rounded-full bg-white animate-ping" />
                  NEW
                </span>
              </p>
              <h2
                className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-[#09090B] font-heading mt-2"
                data-testid="fresh-rail-heading"
              >
                {items.length} New Medical Colleges · {ayLabel}
              </h2>
              <p className="text-sm text-zinc-500 mt-1">
                Newly approved by NMC this session — apply now via NEET counselling
              </p>
            </div>
            <button
              onClick={() => navigate("/stats?tab=new")}
              className="hidden sm:inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-[#6043EF] hover:underline shrink-0"
              data-testid="fresh-rail-viewall"
            >
              View all <ArrowRight size={12} weight="bold" />
            </button>
          </div>

          {/* Netflix-style horizontal scroll */}
          <div
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 sm:-mx-6 px-4 sm:px-6 scroll-smooth [scrollbar-width:thin]"
            data-testid="fresh-rail-track"
          >
            {items.map((c) => (
              <PropertyCard
                key={c.college_code}
                c={c}
                onEnquire={setEnquireCollege}
                onOpen={(col) => navigate(`/colleges/${encodeURIComponent(col.college_code)}`)}
              />
            ))}
          </div>
        </div>
      </section>

      <LeadForm
        open={!!enquireCollege}
        onOpenChange={(v) => { if (!v) setEnquireCollege(null); }}
        college={enquireCollege}
      />
    </>
  );
};

export default FreshCollegesRail;
